import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import makeStyles from '@mui/styles/makeStyles';
import merge from 'lodash/merge';
import { Fragment, useContext, useEffect, useState } from 'react';
import { MAP_LAYER_TYPES } from '@/components/Map/Utils/layerTypes';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { defaultLabelLayerFilter, MAP_LAYER_IDS } from '../../../components/Map/Utils/constants';
import { buildLabelsLayerId } from '../../../components/Map/Utils/layers';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { defaultDimensions } from '../../../utils/analytics';
import { getUserId } from '../../../utils/auth';
import {
  ANALYTICS_EVENT_ACTIONS,
  ANALYTICS_EVENTS,
  LEASE_COUNTRY_ISO_CODES,
} from '../../../utils/constants';
import {
  ASSETS_LABEL,
  BASINS_LABEL,
  BLOCKS_LABEL,
  EA_WELLS_LABEL,
  EXPLORATION_LABEL,
  FACILITIES_LABEL,
  FIELDS_LABEL,
  GAS_LABEL,
  INJECTION_LABEL,
  LABEL,
  LICENSED_LABEL,
  LIQUEFACTION_LABEL,
  LNG_FACILITIES_LABEL,
  OIL_LABEL,
  OPERATOR_LABEL,
  OTHER_LABEL,
  PIPELINE_SEGMENTS_LABEL,
  PIPELINES_LABEL,
  PRODUCTION_LABEL,
  PRODUCTS_LABEL,
  REGASIFICATION_LABEL,
  UNLICENSED_LABEL,
  WELLS_LABEL,
} from '../constants';
import MapFilterContext from './filterContext';

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.palette.mapTab,
    ...theme.palette.scrollbar,
    padding: '5px',
  },
  radioChildrenContainer: {
    paddingLeft: '15px',
  },
  radio: {
    color: '#2a2e49',
    '&.Mui-checked': {
      color: '#2a2e49',
    },
  },
  formRoot: {
    margin: '5px 0',
  },
}));

const BASE_LAYERS = {
  [BLOCKS_LABEL]: {
    label: BLOCKS_LABEL,
    children: {
      [LICENSED_LABEL]: {
        label: LICENSED_LABEL,
        checked: true,
        tag: LICENSED_LABEL,
        customFilter: ['==', ['get', 'licenseStatus'], LICENSED_LABEL],
      },
      [UNLICENSED_LABEL]: {
        label: UNLICENSED_LABEL,
        checked: true,
        tag: 'Unlicensed',
        // If licenseStatus is null or empty we attribute it as 'Unlicensed'
        customFilter: [
          'case',
          ['!', ['has', 'licenseStatus']],
          true,
          ['==', ['get', 'licenseStatus'], ''],
          true,
          ['==', ['get', 'licenseStatus'], UNLICENSED_LABEL],
          true,
          false,
        ],
      },
      [LABEL]: {
        label: LABEL,
        checked: true,
        tag: buildLabelsLayerId('blocks'),
        addDivider: true,
        customFilter: 'labels',
      },
      [OPERATOR_LABEL]: {
        label: OPERATOR_LABEL,
        checked: false,
        tag: buildLabelsLayerId('blocks'),
        customFilter: 'labels',
      },
    },
    customFilter: filters => {
      // Remove labels layer from filter
      const newFilters = filters.filter(filter => filter !== 'labels');
      return newFilters.length > 0
        ? // biome-ignore lint/performance/noAccumulatingSpread: Supress accumulating spread
          ['case', ...newFilters.reduce((acc, filter) => [...acc, filter, true], []), false]
        : ['match', ['get', 'licenseStatus'], ['null'], true, false];
    },
    childrenTag: 'licenseStatus',
    checked: true,
    tag: 'blocks',
  },
  [ASSETS_LABEL]: {
    label: ASSETS_LABEL,
    children: {
      [LABEL]: {
        label: LABEL,
        checked: true,
        tag: buildLabelsLayerId('assets'),
      },
      [OPERATOR_LABEL]: {
        label: OPERATOR_LABEL,
        checked: false,
        tag: buildLabelsLayerId('assets'),
      },
    },
    checked: true,
    tag: 'assets',
  },
  [FIELDS_LABEL]: {
    label: FIELDS_LABEL,
    children: {
      Labels: {
        label: LABEL,
        checked: true,
        tag: buildLabelsLayerId('fields'),
      },
    },
    checked: true,
    tag: 'fields',
  },
  [WELLS_LABEL]: {
    label: WELLS_LABEL,
    children: {
      [EXPLORATION_LABEL]: {
        label: EXPLORATION_LABEL,
        checked: true,
        tag: EXPLORATION_LABEL,
      },
      [INJECTION_LABEL]: {
        label: INJECTION_LABEL,
        checked: true,
        tag: INJECTION_LABEL,
      },
      [PRODUCTION_LABEL]: {
        label: PRODUCTION_LABEL,
        checked: true,
        tag: PRODUCTION_LABEL,
      },
      [OTHER_LABEL]: {
        label: OTHER_LABEL,
        checked: true,
        tag: OTHER_LABEL,
      },
      [LABEL]: {
        label: LABEL,
        checked: true,
        tag: buildLabelsLayerId('wells'),
        addDivider: true,
      },
    },
    childrenTag: 'weaClassification',
    checked: true,
    tag: 'wells',
  },
  [EA_WELLS_LABEL]: {
    label: EA_WELLS_LABEL,
    children: {
      Labels: {
        label: LABEL,
        checked: true,
        tag: MAP_LAYER_IDS.eaWellLabels,
        hasNoTileset: true,
      },
    },
    checked: true,
    tag: 'eawells',
    skipCountryTilesets: true,
  },
  [BASINS_LABEL]: {
    label: BASINS_LABEL,
    children: {
      [LABEL]: {
        label: LABEL,
        checked: false,
        tag: MAP_LAYER_IDS.basinsLabels,
        hasNoTileset: true,
      },
    },
    checked: false,
    tag: MAP_LAYER_IDS.basins,
    skipCountryTilesets: true,
  },
  [FACILITIES_LABEL]: {
    label: FACILITIES_LABEL,
    children: null,
    checked: true,
    tag: 'facilities',
  },
  [LNG_FACILITIES_LABEL]: {
    label: LNG_FACILITIES_LABEL,
    children: {
      Liquefaction: {
        label: LIQUEFACTION_LABEL,
        checked: true,
        tag: 'lng_liquefaction',
      },
      Regasification: {
        label: REGASIFICATION_LABEL,
        checked: true,
        tag: 'lng_regasification',
      },
    },
    childrenTag: 'category',
    checked: true,
    tag: MAP_LAYER_IDS.lngFacilities,
    skipCountryTilesets: true,
  },
  [PIPELINES_LABEL]: {
    label: PIPELINES_LABEL,
    children: {
      [GAS_LABEL]: {
        label: GAS_LABEL,
        checked: true,
        tag: GAS_LABEL,
      },
      [OIL_LABEL]: {
        label: OIL_LABEL,
        checked: true,
        tag: OIL_LABEL,
      },
      [PRODUCTS_LABEL]: {
        label: PRODUCTS_LABEL,
        checked: true,
        tag: PRODUCTS_LABEL,
      },
      'Other (grey)': {
        label: `${OTHER_LABEL} (grey)`,
        checked: true,
        tag: OTHER_LABEL,
      },
    },
    childrenTag: 'product',
    checked: true,
    tag: 'pipelines',
  },
  [PIPELINE_SEGMENTS_LABEL]: {
    label: PIPELINE_SEGMENTS_LABEL,
    children: {
      [GAS_LABEL]: {
        label: GAS_LABEL,
        checked: true,
        tag: GAS_LABEL,
      },
      [OIL_LABEL]: {
        label: OIL_LABEL,
        checked: true,
        tag: OIL_LABEL,
      },
    },
    childrenTag: 'hydrocarbon',
    checked: true,
    tag: 'pipeline_segments',
  },
  [OTHER_LABEL]: {
    label: OTHER_LABEL,
    children: null,
    checked: true,
    tag: 'formations',
  },
};

export default ({ map, hidden }) => {
  const { currentUser } = useCurrentUser();
  const { showEaWells, setShowEaWells } = useContext(MapFilterContext);
  const classes = useStyles();
  const { mapboxPoc } = useGetFeatureFlags();

  const [layers, setLayers] = useState(merge({}, map?.myLayers || BASE_LAYERS));

  const [userProfile, setUserProfile] = useState({});

  useEffect(() => {
    if (currentUser?.mapTilesetSources) {
      setUserProfile(currentUser);
    }
  }, [currentUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(layers): suppress dependency of layers
  useEffect(() => {
    // Only run if map is available
    if (!map) {
      return;
    }

    // Add a longer delay to ensure layers are created and available
    const timeoutId = setTimeout(() => {
      // Toggle the visibility of the E&A wells labels along with the wells themselves:
      if (map?.getLayer(MAP_LAYER_IDS.eaWellLabels)) {
        map.setLayoutProperty(
          MAP_LAYER_IDS.eaWellLabels,
          'visibility',
          showEaWells ? 'visible' : 'none',
        );
      } else {
        // Retry after another delay if layer not found
        setTimeout(() => {
          if (map?.getLayer(MAP_LAYER_IDS.eaWellLabels)) {
            map.setLayoutProperty(
              MAP_LAYER_IDS.eaWellLabels,
              'visibility',
              showEaWells ? 'visible' : 'none',
            );
          }
        }, 500);
      }
      // Also toggle the visibility of the E&A wells layer itself:
      if (map?.getLayer(MAP_LAYER_IDS.eaWells)) {
        map.setLayoutProperty(
          MAP_LAYER_IDS.eaWells,
          'visibility',
          showEaWells ? 'visible' : 'none',
        );
      } else {
        // Retry after another delay if layer not found
        setTimeout(() => {
          if (map?.getLayer(MAP_LAYER_IDS.eaWells)) {
            map.setLayoutProperty(
              MAP_LAYER_IDS.eaWells,
              'visibility',
              showEaWells ? 'visible' : 'none',
            );
          }
        }, 500);
      }

      // Update the layers state to reflect the current visibility
      const newLayers = {
        ...layers,
      };
      const newChild = newLayers[EA_WELLS_LABEL].children.Labels;
      newChild.checked = showEaWells;
      setLayers(newLayers);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [showEaWells, map]); // Add map to dependencies

  const hasLabels = layer => {
    return ['blocks', 'assets', 'fields', 'wells'].includes(layer);
  };

  const hasOperatorLabels = layer => {
    return ['blocks', 'assets'].includes(layer);
  };

  const toggleParentLayersPoc = layer => {
    if (!map) return;

    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'Maps',
      event_action: ANALYTICS_EVENT_ACTIONS.layer,
      event_label: layer.label,
      userId: getUserId(),
      ...defaultDimensions,
    });

    const checked = !layer.checked;
    const newLayers = {
      ...layers,
    };
    const newLayer = newLayers[layer.label];
    newLayer.checked = checked;

    if (layer.children) {
      Object.values(newLayer.children).forEach(newChild => {
        newChild.checked = checked;
      });
    }

    const vis = checked ? 'visible' : 'none';

    if (layer.tag === 'eawells') {
      // The E&A wells layer is a special case: we share the state of the checkbox with the
      // "Screening" tab, so we need to get/set the value using the method passed in as a prop.
      setShowEaWells(showEaWells => !showEaWells);
      return;
    } else {
      // Use the DynamicMap's toggleLayer method for proper label handling
      if (map.layerCallbacks?.toggleLayer) {
        // For DynamicMap, layer.tag is the simple name, but actual layer ID is country-prefixed
        map.layerCallbacks.toggleLayer([layer.tag], vis);
      } else {
        // Fallback to direct layer manipulation
        map.setFilter(layer.tag, null);
        map.setLayoutProperty(layer.tag, 'visibility', vis);

        // Handle labels manually if toggleLayer is not available
        if (hasLabels(layer.tag)) {
          const labelLayerId = buildLabelsLayerId(layer.tag);
          if (map.getLayer(labelLayerId)) {
            map.setLayoutProperty(labelLayerId, 'visibility', vis);
          }
        }
      }
    }

    map.myLayers = newLayers;
    setLayers(newLayers);
  };

  const toggleParentLayers = layer => {
    if (!map) return;

    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'Maps',
      event_action: ANALYTICS_EVENT_ACTIONS.layer,
      event_label: layer.label,
      userId: getUserId(),
      ...defaultDimensions,
    });

    const checked = !layer.checked;
    const newLayers = {
      ...layers,
    };
    const newLayer = newLayers[layer.label];
    newLayer.checked = checked;

    if (layer.children) {
      Object.values(newLayer.children).forEach(newChild => {
        newChild.checked = checked;
      });
    }

    const vis = checked ? 'visible' : 'none';
    if ([MAP_LAYER_IDS.basins, MAP_LAYER_IDS.lngFacilities].includes(layer.tag)) {
      map.setFilter(layer.tag, null);
      map.setLayoutProperty(layer.tag, 'visibility', vis);

      if (layer.tag === MAP_LAYER_IDS.basins) {
        map.setLayoutProperty(MAP_LAYER_IDS.basinsLabels, 'visibility', vis);
      }
    } else if (layer.tag === 'eawells') {
      // The E&A wells layer is a special case: we share the state of the checkbox with the
      // "Screening" tab, so we need to get/set the value using the method passed in as a prop.
      setShowEaWells(showEaWells => !showEaWells);
      return;
    } else {
      const userTileSources = userProfile.mapTilesetSources;

      for (const source of userTileSources) {
        let regex;
        if (layer.tag === 'blocks' && LEASE_COUNTRY_ISO_CODES.includes(source.country)) {
          regex = /.*_(leases|blocks)/g;
        } else {
          regex = new RegExp(`.*_${layer.tag}`, 'g');
        }
        const layerNames = source.layers.filter(l => l.match(regex));
        layerNames.forEach(layerName => {
          if (layerName !== undefined) {
            map.setFilter(layerName, null);
            map.setLayoutProperty(layerName, 'visibility', vis);
            if (hasLabels(layer.tag)) {
              // Toggle the visibility of the labels along with the layer, and clear any filters:
              const labelLayerName = buildLabelsLayerId(layerName);
              const textField = hasOperatorLabels(layer.tag) ? 'nameAndOperator' : 'name';
              map.setLayoutProperty(labelLayerName, 'text-field', ['get', textField]);
              map.setLayoutProperty(labelLayerName, 'visibility', vis);
              map.setFilter(labelLayerName, defaultLabelLayerFilter);
            }
          }
        });
        // if layer is other/formations attempt to toggle presalt_polygons
        if (layer.tag === 'formations') {
          const presaltLayer = source.layers.find(l => l.match(/.*_presalt_polygons/g));
          if (presaltLayer) {
            map.setLayoutProperty(`${presaltLayer}`, 'visibility', vis);
          }
        }
      }
    }
    map.myLayers = newLayers;
    setLayers(newLayers);
  };

  const toggleChildLayersPoc = (layer, child) => {
    if (!map) return;

    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'Maps',
      event_action: ANALYTICS_EVENT_ACTIONS.layer,
      event_label: child.label,
      userId: getUserId(),
      ...defaultDimensions,
    });

    const checked = !child.checked;
    const newLayers = {
      ...layers,
    };
    const newLayer = newLayers[layer.label];
    newLayer.children[child.label].checked = checked;
    newLayers[layer.label] = newLayer;
    let vis = checked ? 'visible' : 'none';
    let labelText = 'name';
    // Handle label toggling specifically
    if (
      (child.label === LABEL && hasOperatorLabels(layer.tag)) ||
      (child.label === OPERATOR_LABEL && hasLabels(layer.tag))
    ) {
      const labelLayerChecked = newLayer.children[LABEL].checked;
      const operatorLabelLayerChecked = newLayer.children[OPERATOR_LABEL].checked;
      if (operatorLabelLayerChecked && labelLayerChecked) {
        labelText = 'nameAndOperator';
        vis = 'visible';
      } else if (operatorLabelLayerChecked && !labelLayerChecked) {
        labelText = 'operator';
        vis = 'visible';
      } else if (!operatorLabelLayerChecked && labelLayerChecked) {
        labelText = 'name';
        vis = 'visible';
      }
    }

    // Handle label toggling specifically
    if (child.label === LABEL || child.label === OPERATOR_LABEL) {
      const labelLayerId = buildLabelsLayerId(layer.tag);
      if (map.getLayer(labelLayerId)) {
        map.setLayoutProperty(labelLayerId, 'text-field', ['get', labelText]);
        map.setLayoutProperty(labelLayerId, 'visibility', vis);

        // If toggling the blocks label layer, also toggle the lease label layer
        if (layer.tag === MAP_LAYER_TYPES.blocks) {
          const leasesLayerId = buildLabelsLayerId(MAP_LAYER_TYPES.leases);
          map.setLayoutProperty(leasesLayerId, 'text-field', ['get', labelText]);
          map.setLayoutProperty(leasesLayerId, 'visibility', vis);
        }
      }
      // Update state before returning
      map.myLayers = newLayers;
      setLayers(newLayers);
      return;
    }

    // Create a filter for the child layers:
    // e.g. the wells are grouped by their classification (Exploration, Production, ...),
    // but the layer includes all classifications, so we need to filter out the ones that
    // are not checked.
    let newFilter;
    if (layer.childrenTag) {
      let filters = Object.values(newLayer.children)
        .filter(newChild => newChild.checked)
        .map(newChild => (newChild.customFilter ? newChild.customFilter : newChild.tag));
      if (layer.customFilter && filters.length !== 0) {
        newFilter = layer.customFilter(filters);
      } else {
        if (filters.length === 0) {
          filters = ['']; // if no children are checked, show nothing
        }
        newFilter = ['match', ['get', layer.childrenTag], filters, true, false];
      }
    }

    // For other child layers (Exploration, Production, etc.), apply the filter
    if (newFilter && map.getLayer(layer.tag)) {
      map.setFilter(layer.tag, newFilter);
      map.setLayoutProperty(layer.tag, 'visibility', 'visible');

      // Also apply the filter to the label layer if it exists
      if (hasLabels(layer.tag)) {
        const labelLayerId = buildLabelsLayerId(layer.tag);
        if (map.getLayer(labelLayerId)) {
          map.setFilter(labelLayerId, newFilter);
        } else {
          console.warn(`Label layer ${labelLayerId} not found for layer ${layer.tag}`);
        }
      }
    }

    map.myLayers = newLayers;
    setLayers(newLayers);
  };

  const toggleChildLayers = (layer, child) => {
    if (!map) return;

    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: 'Maps',
      event_action: ANALYTICS_EVENT_ACTIONS.layer,
      event_label: child.label,
      userId: getUserId(),
      ...defaultDimensions,
    });

    const checked = !child.checked;
    const newLayers = {
      ...layers,
    };
    const newLayer = newLayers[layer.label];
    newLayer.children[child.label].checked = checked;
    newLayers[layer.label] = newLayer;
    let vis = checked ? 'visible' : 'none';
    let labelText = 'name';
    if (
      (child.label === LABEL && hasOperatorLabels(layer.tag)) ||
      (child.label === OPERATOR_LABEL && hasLabels(layer.tag))
    ) {
      const labelLayerChecked = newLayer.children[LABEL].checked;
      const operatorLabelLayerChecked = newLayer.children[OPERATOR_LABEL].checked;
      if (operatorLabelLayerChecked && labelLayerChecked) {
        labelText = 'nameAndOperator';
        vis = 'visible';
      } else if (operatorLabelLayerChecked && !labelLayerChecked) {
        labelText = 'operator';
        vis = 'visible';
      } else if (!operatorLabelLayerChecked && labelLayerChecked) {
        labelText = 'name';
        vis = 'visible';
      }
    }

    // Create a filter for the child layers:
    // e.g. the wells are grouped by their classification (Exploration, Production, ...),
    // but the layer includes all classifications, so we need to filter out the ones that
    // are not checked.
    let newFilter;
    if (layer.childrenTag) {
      let filters = Object.values(newLayer.children)
        .filter(newChild => newChild.checked)
        .map(newChild => (newChild.customFilter ? newChild.customFilter : newChild.tag));
      if (layer.customFilter && filters.length !== 0) {
        newFilter = layer.customFilter(filters);
      } else {
        if (filters.length === 0) {
          filters = ['']; // if no children are checked, show nothing
        }
        newFilter = ['match', ['get', layer.childrenTag], filters, true, false];
      }
    }

    const applyFilter = layerName => {
      // If the filter is present, make sure the parent layer is visible and set the filter:
      if (newFilter) {
        map.setLayoutProperty(layerName, 'visibility', 'visible');
        map.setFilter(layerName, newFilter);
      }
    };

    if (!layer.skipCountryTilesets) {
      for (const source of userProfile.mapTilesetSources) {
        const layerName = source.layers.find(l => l.indexOf(layer.tag) !== -1);
        if (layerName !== undefined) {
          if (hasLabels(layer.tag)) {
            const labelLayerName = buildLabelsLayerId(layerName);

            // If the filter is present, we need to update the filter for the labels layer:
            // e.g. if we're unchecking the "Exploration" wells, we need to update the filter
            // for the "Exploration" labels.
            if (newFilter) {
              const combinedFilter = [...defaultLabelLayerFilter, newFilter];
              map.setFilter(labelLayerName, combinedFilter);
            }

            // Only toggle the visibility of the labels if the selected child is "Labels":
            if (child.label === LABEL || child.label === OPERATOR_LABEL) {
              // If block labels were toggled, toggle the lease label layer as well
              if (layer.tag === 'blocks' && LEASE_COUNTRY_ISO_CODES.includes(source.country)) {
                const leasesLayerName = buildLabelsLayerId(`${source.country}_leases`);
                map.setLayoutProperty(leasesLayerName, 'text-field', ['get', labelText]);
                map.setLayoutProperty(leasesLayerName, 'visibility', vis);
              }

              map.setLayoutProperty(labelLayerName, 'text-field', ['get', labelText]);
              map.setLayoutProperty(labelLayerName, 'visibility', vis);
            }
          }

          applyFilter(layerName);
        }
      }
    }

    // For layers that don't have a tileset, we need to toggle the visibility of the layer itself.
    // e.g. E&A wells
    if (child.hasNoTileset) {
      map.setLayoutProperty(child.tag, 'visibility', vis);
    }

    // The LNG facilities layer is not part of the user's tilesets, so we need to handle it separately:
    if (layer.tag === MAP_LAYER_IDS.lngFacilities) {
      applyFilter(layer.tag);
    }

    map.myLayers = newLayers;
    setLayers(newLayers);
  };

  if (hidden) {
    return null;
  }

  return (
    <div className={classes.root}>
      {Object.values(layers).map(layer => {
        const checked = layer.tag === 'eawells' ? showEaWells : layer.checked;
        return (
          <FormControl key={layer.label} className={classes.formRoot} component='fieldset'>
            <RadioGroup
              aria-label='layerFilter'
              name='layerFilter'
              id={`${layer.label.replace(/[^a-zA-Z0-9]/g, '-')}-radio-group`}
              value={checked}
              onClick={e => {
                e.preventDefault();
                mapboxPoc ? toggleParentLayersPoc(layer) : toggleParentLayers(layer);
              }}
            >
              <FormControlLabel
                checked={checked}
                value={true}
                control={<Radio className={classes.radio} />}
                label={`${layer.label}`}
              />
              <FormControl className={classes.radioChildrenContainer} component='fieldset'>
                {layer.children
                  ? Object.values(layer.children).map((child, i) => (
                      <Fragment key={`${child.label}-${i}`}>
                        {child.addDivider && <Divider />}
                        <RadioGroup
                          key={child.label}
                          aria-label='layerSubFilter'
                          name='layerSubFilter'
                          value={child.checked}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            mapboxPoc
                              ? toggleChildLayersPoc(layer, child)
                              : toggleChildLayers(layer, child);
                          }}
                        >
                          <FormControlLabel
                            checked={child.checked}
                            value={true}
                            control={<Radio className={classes.radio} />}
                            label={`${child.label}`}
                          />
                        </RadioGroup>
                      </Fragment>
                    ))
                  : null}
              </FormControl>
            </RadioGroup>
            <Divider />
          </FormControl>
        );
      })}
    </div>
  );
};
